import {useConfig} from '@aboutyou/config';
import {useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {NewsletterSubscriptionService_SubscribeToNewsletterLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.grpc.lazy';
import {Gender} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useContactRoute,
  useHistory,
  useInitialLocation,
  usePrivacyPolicyRoute,
} from '@aboutyou/router';
import {useTrackOverlay} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  ContentPopper,
  ContentPopperProps,
  Disclaimer,
  FONT_SIZES,
  ModalDialog,
  ModalDialogBodyContent,
  SPACING,
  Text,
  TextInput,
  Toggle,
  Z_INDEXES,
  mobileOnlyLinaria,
  useShowToast,
} from '@aboutyou/ui-components';
import {
  UserGenders,
  useOptionalSession,
  useUserEmail,
  useUserGender,
} from '@aboutyou/user';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useCallback, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {useSessionGender} from '../../../shared-deprecated/sessionGender';
import {useNewsLetterSignUpStateManager} from '../useNewsLetterSignUpStateManager';

export let GUEST_USER_SUBSCRIBED_TO_NEWSLETTER = false;

export const NewsletterSignUp: FC<{
  NewsletterSubscriptionComponent: ContentPopperProps['LayoutComponent'];
  fetchNotifications: VoidFunction;
}> = ({NewsletterSubscriptionComponent, fetchNotifications}) => {
  const {t} = useTranslation();
  const {cancel, isOpen, hide} = useNewsLetterSignUpStateManager();
  const initialLocation = useInitialLocation();
  const {gender} = useSessionGender();
  const trackNewsletterOverlay = useTrackNewsletterOverlay(gender);

  const {push, location} = useHistory();

  const isModalDialog = NewsletterSubscriptionComponent === ModalDialog;

  const onClose = () => {
    trackNewsletterOverlay('reject');
    closeNewsletterOverlaySignUp();
  };

  const closeNewsletterOverlaySignUp = () => {
    if (location.search.includes('newsletterSubscription')) {
      push('/');
    } else if (
      // On desktop, when the entry page is the deep link "/newsletter/opt-in" and the gender is kids, the user gets
      // in an infinite loop when closing the newsletter, since history back is used, sending the user
      // to the home page, and then the kids category page redirect kicks in
      initialLocation.pathname === '/newsletter/opt-in' &&
      gender === 'kids' &&
      isModalDialog
    ) {
      hide();
    } else {
      cancel();
    }
  };

  return (
    <StyledContentPopper
      LayoutComponent={NewsletterSubscriptionComponent}
      data-testid="newsletterSignUp"
      renderInPortal
      onClose={onClose}
      open={isOpen}
      fixedSmallWidth={false}
      contentFitWidth
      data-enable-max-width={isModalDialog}
      headerContent={
        <HeaderWrapper>
          <Text size="px18" weight="mediumHeavy">
            {t('Stay up to date with our Newsletter')}
          </Text>
        </HeaderWrapper>
      }
      bodyContent={
        <BodyContent
          closeNewsletterOverlaySignUp={closeNewsletterOverlaySignUp}
          fetchNotifications={fetchNotifications}
        />
      }
    />
  );
};

const BodyContent: FC<{
  closeNewsletterOverlaySignUp: VoidFunction;
  fetchNotifications: VoidFunction;
}> = ({closeNewsletterOverlaySignUp, fetchNotifications}) => {
  const privacyPolicyRoute = usePrivacyPolicyRoute();
  const contactRoute = useContactRoute();
  const {t} = useTranslation();
  const {isOpen} = useNewsLetterSignUpStateManager();
  const session = useOptionalSession();
  const userEmail = useUserEmail();
  const [acceptSubscription, setAcceptSubscription] = useState(true);
  const {isAuthenticated} = useIsAuthenticated();
  const userGender = useUserGender();
  const sessionGender = useSessionGender().gender;
  const gender = isAuthenticated ? userGender : sessionGender;
  const showToast = useShowToast();
  const trackNewsletterOverlay = useTrackNewsletterOverlay(gender);
  const {
    getValues,
    watch,
    reset,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<{
    email: string;
    acceptSubscription: boolean;
  }>({
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    trackNewsletterOverlay('impression');
  }, []);

  const watchEmail = watch('email');

  const genderRequestType = (() => {
    if (!gender || gender === 'kids') {
      return Gender.FEMALE;
    }

    return gender === UserGenders.Female ? Gender.FEMALE : Gender.MALE;
  })();

  const resetFormState = () => {
    reset();
    setAcceptSubscription(true);
  };

  const subscriptToNewsletterCallback = useGrpcRequestCallback(
    NewsletterSubscriptionService_SubscribeToNewsletterLazy,
    {
      disabled: !isOpen,
    },
  );

  const onSuccess = () => {
    showToast({
      content: (
        <SuccessMessageWrapper>
          <SuccessText
            size="px12"
            weight="mediumHeavy"
            dangerouslySetInnerHTML={{
              __html: t(
                '<strong>Just one step left:</strong> To complete the process please click the link in the email',
              ),
            }}
          />
        </SuccessMessageWrapper>
      ),
      type: 'SUCCESS',
      'data-testid': 'subscribeToNewsletterSuccessMessage',
    });

    if (!isAuthenticated) {
      GUEST_USER_SUBSCRIBED_TO_NEWSLETTER = true;
    }

    trackNewsletterOverlay('accept');

    resetFormState();
    fetchNotifications();
    closeNewsletterOverlaySignUp();
  };

  const onError = () => {
    showToast({
      content: t('Oops! Something went wrong!'),
      type: 'ERROR',
      'data-testid': 'subscribeToNewsletterErrorMessage',
    });
  };

  const submitHandler = useCallback(
    ({email}: {email: string}) => {
      subscriptToNewsletterCallback({
        email: userEmail ?? email,
        gender: Gender.FEMALE,
        session,
      })
        .then(onSuccess)
        .catch(onError);
    },
    [genderRequestType, session],
  );

  const handleOnClose = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    trackNewsletterOverlay('reject');
    closeNewsletterOverlaySignUp();
  };

  const disableSubmitButton =
    !acceptSubscription || (watchEmail === '' && !userEmail);

  return (
    <Form
      autoComplete="on"
      method="POST"
      onSubmit={handleSubmit(submitHandler)}
    >
      <Headline size="px14" weight="mediumHeavy">
        {t('Get updates to sales, trends and drops')}
      </Headline>
      <Content>
        <InnerContent>
          <TextWrapper>
            <Text size="px14" weight="mediumBold">
              {t('Yes, sign me up for the Newsletter')}
            </Text>
            {userEmail && (
              <UserEmailText size="px12" weight="mediumBold" color="Grey0">
                {userEmail}
              </UserEmailText>
            )}
          </TextWrapper>
          <Toggle
            checked={acceptSubscription}
            {...register('acceptSubscription')}
            onChange={() => {
              setAcceptSubscription(prev => !prev);
            }}
            aria-label={
              acceptSubscription
                ? t('Reject subscription')
                : t('Accept subscription')
            }
          />
        </InnerContent>
        <div>
          {!userEmail && (
            <EmailInput
              type="email"
              autoComplete="email"
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Grey3,
              }}
              label={t('Your email address')}
              {...register('email', {
                required: t('Please enter your email address'),
                pattern: {
                  value: emailRegex,
                  message: t('This is not a valid email address'),
                },
              })}
              errorMessage={errors.email?.message}
              value={getValues('email')}
            />
          )}
        </div>
        <StyledDisclaimer
          data-testid="disclaimer"
          text={t(
            'I would like to receive the ABOUT YOU newsletter on trends and offers (see {privacyPolicy}) by email. The consent can be withdrawn at any time via a newsletter email or customer service ({helpCenter}).',
            {
              privacyPolicy: `<a rel="noopener noreferrer" target="_blank" href="${privacyPolicyRoute}#section3.3.2.">${t(
                'Privacy Policy',
              )}</a>`,
              helpCenter: `<a rel="noopener noreferrer" target="_blank" href="${contactRoute}">${t(
                'Contact us | ABOUT YOU Help Center',
              )}</a>`,
            },
          )}
        />
      </Content>
      <Footer>
        <CancelButton
          variant="secondary"
          onClick={handleOnClose}
          data-testid="newsletterSignUpCloseButton"
          size="fit-width"
        >
          {t('Cancel')}
        </CancelButton>
        <SignUpButton
          variant="primary"
          disabled={disableSubmitButton}
          data-testid="newsletterSignUpSubmitButton"
          size="fit-width"
        >
          {t('Sign up')}
        </SignUpButton>
      </Footer>
    </Form>
  );
};

const useTrackNewsletterOverlay = (gender?: string) => {
  const trackOverlay = useTrackOverlay();
  const {device} = useConfig();

  return useCallback(
    (eventType: 'impression' | 'accept' | 'reject') => {
      trackOverlay({
        action: eventType,
        content_type: device === 'desktop' ? 'modal' : 'bottom_sheet',
        name: 'newsletter',
        context: gender === UserGenders.Female ? 'female' : 'male',
      });
    },
    [trackOverlay, device, gender],
  );
};

const StyledContentPopper = styled(ContentPopper)`
  z-index: ${Z_INDEXES.NEWSLETTER_SUBSCRIPTION_CONTENT_POPPER};

  &[data-enable-max-width='true'] > div {
    max-width: 440px;
  }

  ${ModalDialogBodyContent} {
    overflow-x: hidden;
  }
`;

const Form = styled.form`
  ${mobileOnlyLinaria(`
    margin: -16px;
  `)}
`;

const HeaderWrapper = styled.div`
  text-align: center;
  position: relative;
  width: 100%;

  ${mobileOnlyLinaria(`
    margin-top: 5px;
  `)}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;

  ${mobileOnlyLinaria(`
    padding: 20px;
  `)}
`;

const InnerContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  gap: 20px;
  // the below styles are placed to add a divider, since the footer needs to be part of the body because of the form tag on the newsletter
  border-top: 1px solid ${COLORS.Grey4};
  margin-inline: -24px;
  padding: 20px 24px 0;

  ${mobileOnlyLinaria(`
    padding: 20px;
    margin-inline: -0;
  `)}
`;

const Headline = styled(Text)`
  display: block;
  line-height: 1.42;

  ${mobileOnlyLinaria(`
    padding: 24px 20px 0 20px;
  `)}
`;

const UserEmailText = styled(Text)`
  line-height: 1.5;
`;

const CancelButton = styled(Button)`
  &&& {
    padding-inline: 32px;
  }
`;

const SignUpButton = styled(Button)`
  flex: 1;
  padding-inline: 0;
`;

const EmailInput = styled(TextInput)`
  margin-top: 16px;
  width: 100%;
`;

const SuccessMessageWrapper = styled.span`
  display: flex;
  gap: 8px;
`;

const SuccessText = styled(Text)`
  line-height: 1.5;
  text-align: left;
`;

const StyledDisclaimer = styled(Disclaimer)`
  font-size: ${FONT_SIZES.px10};
  text-align: left;
  line-height: 1.6;
  margin-top: ${SPACING['Y.Lg']};
  color: ${COLORS['Text.Grey']};
`;
